import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@env';
import { UserService } from '@features/user/services/user.service';
import { DiscountHelperService } from '@shared/services/discount-helper.service';
import { BehaviorSubject, map, shareReplay, take } from 'rxjs';

const apiUrl = environment.apiUrl + 'user/cart';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  cart: BehaviorSubject<any> = new BehaviorSubject({
    cartItems: null,
    countCartItems: 0,
    totalCartPrice: 0,
    totalCartWeight: 0,
  });

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private http: HttpClient,
    private discountHelper: DiscountHelperService,
    private userService: UserService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // note: need refactor, handle cart sync data
      this.userService.currentUserValue.subscribe((user: any) => {
        if (user) {
          this.syncCart();
        } else {
          // clear cart
          this.cart.next({
            cartItems: null,
            countCartItems: 0,
            totalCartPrice: 0,
            totalCartWeight: 0,
          });
        }
      });
    }
  }

  public get cart$() {
    return this.cart;
  }

  syncCart() {
    this.http
      .get(apiUrl)
      .pipe(shareReplay(1))
      .subscribe((res: any) => {
        let cartItems: any[] = res['data'];
        cartItems.forEach((cartItem) =>
          this.calcCustomCartItemAttributes(cartItem)
        );
        const calculatedCartItems = this.calcCartItems(cartItems);
        this.cart.next({
          cartItems: cartItems,
          totalCartItems: calculatedCartItems['totalCartItems'],
          totalCartPrice: calculatedCartItems['totalCartPrice'],
          totalCartWeight: calculatedCartItems['totalCartWeight'],
        });
      });
  }

  calcCustomCartItemAttributes(cartItem: any) {
    cartItem.totalWeight =
      Number(cartItem.unit_weight) * Number(cartItem.quantity);
    cartItem.totalPrice =
      Number(cartItem.unit_price) * Number(cartItem.quantity);
    cartItem.discountPercentage =
      this.discountHelper.calculateDiscountPercentage(
        cartItem.actual_price,
        cartItem.unit_price
      );
  }
  calcCartItems(cartItems: any[]) {
    let totalCartItems: number = 0;
    let totalCartPrice: number = 0;
    let totalCartWeight: number = 0;
    if (cartItems) {
      cartItems.forEach((item) => {
        totalCartItems += Number(item.quantity);
        totalCartPrice += Number(item.totalPrice);
        totalCartWeight += Number(item.unit_weight) * Number(item.quantity);
      });
    }
    return {
      totalCartItems: totalCartItems,
      totalCartPrice: totalCartPrice,
      totalCartWeight: totalCartWeight,
    };
  }
  addCartItem(productId: number, productVariantId: number, quantity: number) {
    let body;
    if (productVariantId) {
      body = {
        product_id: productId,
        product_variant_id: productVariantId,
        quantity: quantity,
      };
    } else {
      body = { product_id: productId, quantity: quantity };
    }
    return this.http.post(apiUrl + '/add', body).pipe(
      take(1),
      map((res: any) => {
        this.syncCart();
        return res;
      })
    );
  }
  bulkAddCartItems(cartItems: any[] = []) {
    let body = { cartItems: cartItems };
    return this.http.post(apiUrl + '/bulk_add', body).pipe(
      take(1),
      map((res) => {
        this.syncCart();
        return res;
      })
    );
  }
  updateCartItem(id: number, quantity: number) {
    let body = { id: id, quantity: quantity };
    return this.http.put(apiUrl + '/update', body).pipe(
      take(1),
      map((res: any) => {
        this.syncCart();
        return res;
      })
    );
  }
  removeCartItem(id: number) {
    return this.http.delete(apiUrl + '/remove/' + id).pipe(
      take(1),
      map((res: any) => {
        this.syncCart();
        return res;
      })
    );
  }
}
